/* global reportCatalogue reportGenerationIndexUrl reportGenerationStoreUrl reportGenerationDeleteUrl reportGenerationDownloadUrl reportChannelName reportActualsChannelName reportCentreIsSuperAdmin reportCentrePerPage reportCentrePruningDays reportFormOptionSources reportCentreInitialTab reportFilterSetsIndexUrl reportFilterSetsStoreUrl reportFilterSetUpdateUrl reportFilterSetDestroyUrl reportCentreIsAdmin reportCentreCurrentUserId reportManagementSubCategories reportSchedulesIndexUrl reportSchedulesStoreUrl reportScheduleUpdateUrl reportScheduleStatusUrl reportScheduleDestroyUrl reportScheduleRunUrl reportScheduleRunsUrl reportSchedulePreviewUrl reportScheduleDefaultTimezone */

import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import GeneratedReportsList from './GeneratedReportsList';
import ReportCatalogue from './ReportCatalogue';
import ScheduledReportsList from './ScheduledReportsList';

import Tabs, { RenderStrategyOnVisit } from 'CommonComponents/Tabs';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';
import { ToastProvider } from 'Context/ToastContext';

const REPORT_TAB_ID = 'report-centre---report';
const GENERATED_TAB_ID = 'report-centre---generated-reports';
const SCHEDULES_TAB_ID = 'report-centre---scheduled-reports';

/**
 * Flatten the nested, re-categorised catalogue (top-level → functional → entries,
 * with Setup & Configuration as a flat array) into a single list of entries. Used
 * to resolve a Re-run target and to derive the Module/Source filter options.
 */
function flattenCatalogue(catalogue) {
    const entries = [];

    Object.values(catalogue || {}).forEach((section) => {
        if (Array.isArray(section)) {
            entries.push(...section);
            return;
        }

        if (section && typeof section === 'object') {
            Object.values(section).forEach((group) => {
                if (Array.isArray(group)) {
                    entries.push(...group);
                }
            });
        }
    });

    return entries;
}

/**
 * Unified Report Centre root. Two tabs: a registry-driven report
 * catalogue ("Report") and the cross-module Generated Reports list. The active
 * tab is controlled so the catalogue's "generate" success, the empty-state CTA,
 * and a row's "Re-run" can move the user between tabs.
 */
export default function ReportCentre({
    catalogue,
    indexUrl,
    storeUrl,
    deleteUrl,
    downloadUrl,
    channelName,
    actualsChannelName,
    isSuperAdmin,
    perPage,
    pruningDays,
    optionSources,
    initialTab,
    filterSetsIndexUrl,
    filterSetsStoreUrl,
    filterSetUpdateUrl,
    filterSetDestroyUrl,
    isAdmin,
    currentUserId,
    managementSubCategories,
    schedulesIndexUrl,
    schedulesStoreUrl,
    scheduleUpdateUrl,
    scheduleStatusUrl,
    scheduleDestroyUrl,
    scheduleRunUrl,
    scheduleRunsUrl,
    schedulePreviewUrl,
    scheduleDefaultTimezone,
}) {
    const resolveInitialTab = () => {
        if (initialTab === 'generated') {
            return GENERATED_TAB_ID;
        }
        if (initialTab === 'schedules') {
            return SCHEDULES_TAB_ID;
        }
        return REPORT_TAB_ID;
    };

    const [activeTab, setActiveTab] = useState(resolveInitialTab);
    const [prefillRequest, setPrefillRequest] = useState(null);

    // Only migrated entries carry a `type`; non-migrated link-outs are interleaved
    // into the catalogue but never re-run or filtered by module.
    const migratedEntries = useMemo(() => flattenCatalogue(catalogue).filter((entry) => entry.type), [catalogue]);

    // The Module/Source filter lists the distinct modules present in the catalogue.
    const modules = useMemo(() => [...new Set(migratedEntries.map((entry) => entry.module).filter(Boolean))], [migratedEntries]);

    const goToGenerated = () => setActiveTab(GENERATED_TAB_ID);
    const goToReport = () => setActiveTab(REPORT_TAB_ID);
    const goToSchedules = () => setActiveTab(SCHEDULES_TAB_ID);

    const handleReRun = (row) => {
        const report = migratedEntries.find((entry) => entry.type === row.type);

        if (!report) {
            return;
        }

        // Context-launched reports re-run on their source screen.
        if (report.launchType === 'open' && report.launchRoute) {
            window.location.href = report.launchRoute;
            return;
        }

        setPrefillRequest({ report, options: row.options || null });
        goToReport();
    };

    // ?configure=<type>&options=<json> opens that report's form pre-filled, as an
    // on-screen report's Reconfigure does. The parameters are then dropped from the URL.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const type = params.get('configure');

        if (!type) {
            return;
        }

        const report = migratedEntries.find((entry) => entry.type === type && entry.launchType === 'configure');
        let options = null;
        try {
            options = JSON.parse(params.get('options') || 'null');
        } catch (error) {
            options = null;
        }

        params.delete('configure');
        params.delete('options');
        const query = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

        if (report) {
            setPrefillRequest({ report, options });
            goToReport();
        }
    }, []);

    const tabs = useMemo(
        () => [
            {
                id: REPORT_TAB_ID,
                title: $t('Report'),
                content: () => (
                    <ReportCatalogue
                        catalogue={catalogue}
                        optionSources={optionSources}
                        storeUrl={storeUrl}
                        filterSetsIndexUrl={filterSetsIndexUrl}
                        filterSetsStoreUrl={filterSetsStoreUrl}
                        filterSetUpdateUrl={filterSetUpdateUrl}
                        filterSetDestroyUrl={filterSetDestroyUrl}
                        isAdmin={isAdmin}
                        currentUserId={currentUserId}
                        onGenerated={goToGenerated}
                        prefillRequest={prefillRequest}
                        onPrefillHandled={() => setPrefillRequest(null)}
                        schedulesStoreUrl={schedulesStoreUrl}
                        schedulePreviewUrl={schedulePreviewUrl}
                        scheduleDefaultTimezone={scheduleDefaultTimezone}
                    />
                ),
            },
            {
                id: GENERATED_TAB_ID,
                title: $t('Generated Reports'),
                content: () => (
                    <GeneratedReportsList
                        indexUrl={indexUrl}
                        deleteUrl={deleteUrl}
                        downloadUrl={downloadUrl}
                        channelName={channelName}
                        actualsChannelName={actualsChannelName}
                        isSuperAdmin={isSuperAdmin}
                        perPage={perPage}
                        pruningDays={pruningDays}
                        modules={modules}
                        subCategories={managementSubCategories}
                        onReRun={handleReRun}
                        onGoToReportTab={goToReport}
                        onGoToSchedules={goToSchedules}
                    />
                ),
            },
            {
                id: SCHEDULES_TAB_ID,
                title: $t('Scheduled Reports'),
                content: () => (
                    <ScheduledReportsList
                        indexUrl={schedulesIndexUrl}
                        updateUrl={scheduleUpdateUrl}
                        statusUrl={scheduleStatusUrl}
                        destroyUrl={scheduleDestroyUrl}
                        runUrl={scheduleRunUrl}
                        runsUrl={scheduleRunsUrl}
                        previewUrl={schedulePreviewUrl}
                        isAdmin={isAdmin}
                        defaultTimezone={scheduleDefaultTimezone}
                        reports={migratedEntries}
                    />
                ),
            },
        ],
        [catalogue, optionSources, prefillRequest, modules, migratedEntries],
    );

    return <Tabs tabs={tabs} activeTab={activeTab} onTabActivate={setActiveTab} renderStrategy={RenderStrategyOnVisit} maxScreenHeight />;
}

if (document.getElementById('ReportCentre')) {
    const rootEl = document.getElementById('ReportCentre');
    ReactDOM.render(
        <App>
            <ToastProvider>
                <ConfirmationModalProvider>
                    <ReportCentre
                        catalogue={reportCatalogue}
                        indexUrl={reportGenerationIndexUrl}
                        storeUrl={reportGenerationStoreUrl}
                        deleteUrl={reportGenerationDeleteUrl}
                        downloadUrl={reportGenerationDownloadUrl}
                        channelName={reportChannelName}
                        actualsChannelName={reportActualsChannelName}
                        isSuperAdmin={reportCentreIsSuperAdmin}
                        perPage={reportCentrePerPage}
                        pruningDays={reportCentrePruningDays}
                        optionSources={reportFormOptionSources}
                        initialTab={reportCentreInitialTab}
                        filterSetsIndexUrl={reportFilterSetsIndexUrl}
                        filterSetsStoreUrl={reportFilterSetsStoreUrl}
                        filterSetUpdateUrl={reportFilterSetUpdateUrl}
                        filterSetDestroyUrl={reportFilterSetDestroyUrl}
                        isAdmin={reportCentreIsAdmin}
                        currentUserId={reportCentreCurrentUserId}
                        managementSubCategories={reportManagementSubCategories}
                        schedulesIndexUrl={reportSchedulesIndexUrl}
                        schedulesStoreUrl={reportSchedulesStoreUrl}
                        scheduleUpdateUrl={reportScheduleUpdateUrl}
                        scheduleStatusUrl={reportScheduleStatusUrl}
                        scheduleDestroyUrl={reportScheduleDestroyUrl}
                        scheduleRunUrl={reportScheduleRunUrl}
                        scheduleRunsUrl={reportScheduleRunsUrl}
                        schedulePreviewUrl={reportSchedulePreviewUrl}
                        scheduleDefaultTimezone={reportScheduleDefaultTimezone}
                    />
                </ConfirmationModalProvider>
            </ToastProvider>
        </App>,
        rootEl,
    );
}
